import { camelToStartCase, dateOf, guid, splitWordsOnUpperCase } from "@repo/helpers";
import { type FilterItem, KeyedArray, type Verbiage } from "@repo/models";

import type { LayerConfiguration, LayerDefinition } from "../../../models/mapConfiguration";

import { snapshotServerFilters } from "../hooks/queries/serverFilters";
import { dateRangeLabel } from "./dateRange";
import { defaultLayerColor } from "./layerAppearance";
import { isOfficeLayer, isPositionLayer, isRetailLayer } from "./layerTypes";

export interface LayerLookupData {
	verbiage?: Verbiage;
	partnerTypes?: KeyedArray<FilterItem>;
	partners?: KeyedArray<FilterItem>;
	companyDivisions?: KeyedArray<FilterItem>;
	companyRegions?: KeyedArray<FilterItem>;
	companyMarkets?: KeyedArray<FilterItem>;
	locationStatuses?: KeyedArray<FilterItem>;
	marketers?: KeyedArray<FilterItem>;
	offices?: KeyedArray<FilterItem>;
	brands?: KeyedArray<FilterItem>;
	channels?: KeyedArray<FilterItem>;
	states?: KeyedArray<FilterItem>;
	users?: KeyedArray<FilterItem>;
}

export class LayerKeyedArray extends KeyedArray<LayerDefinition> {
	constructor(data: LayerDefinition[]) {
		super(data, (l) => l.id);
	}

	newArray(data: LayerDefinition[]) {
		return new LayerKeyedArray(data);
	}
}

const DEFAULT_LAST_POSITIONS = 100;

export function createDefaultLayerFilters() {
	return {
		startDateTime: undefined,
		endDateTime: undefined,
		dateRange: undefined,
		hourLimit: undefined,
		showLines: false,
		lastPositions: DEFAULT_LAST_POSITIONS,
		reps: [],
		machines: [],
		activeMachineOnly: false,
		activeRepOnly: false,
		lastPositionOnly: false,
		minAccuracy: undefined,
		statusIds: [],
		statusId: 0,
		companyDivisionId: null,
		companyDivisionName: null,
		companyDivisionIds: [],
		companyRegionId: null,
		companyRegionName: null,
		companyRegionIds: [],
		companyMarketId: null,
		companyMarketName: null,
		companyMarketIds: [],
		partnerTypeId: null,
		partnerTypeName: null,
		partnerId: null,
		partnerName: null,
		brandId: undefined,
		brandName: undefined,
		brandIds: [],
		channelId: undefined,
		channelName: undefined,
		channelIds: [],
		stateId: undefined,
		stateName: undefined,
		stateIds: [],
		marketerIds: [],
		marketerName: undefined,
		assignedOfficeName: undefined,
		assignedOfficeIds: [],
		assignedUserName: undefined,
		assignedUserIds: [],
		goodSales: undefined,
		phoneCarrierType: "",
		enrollmentId: undefined,
	} satisfies Required<LayerConfiguration>;
}

export function createLayer(layer: Partial<LayerDefinition>, index: number, lookup?: LayerLookupData): LayerDefinition {
	const created: LayerDefinition = {
		...createDefaultLayerFilters(),
		id: guid(),
		name: "",
		itemType: undefined,
		executeCount: 0,
		color: defaultLayerColor(index),
		icon: "pin",
		visible: true,
		ordinal: index,
		...layer,
	};
	return { ...created, name: created.name || buildLayerName(created, lookup) };
}

export function withSavedLayerIds(layers: LayerDefinition[], saved: LayerDefinition[]) {
	const savedIds = new Map(saved.map((layer) => [layer.id, layer.layerId]));
	return layers.map((layer) =>
		savedIds.has(layer.id) && savedIds.get(layer.id) !== layer.layerId
			? { ...layer, layerId: savedIds.get(layer.id) }
			: layer,
	);
}

export function applyFilterChange(
	layer: LayerDefinition,
	name: keyof LayerDefinition,
	value: unknown,
	lookup: LayerLookupData,
): LayerDefinition {
	if ((layer[name] ?? null) === (value ?? null)) return layer;

	let next: LayerDefinition = { ...layer, [name]: value };
	if (name === "itemType") {
		next = { ...layer, ...createDefaultLayerFilters(), itemType: next.itemType };
		next.executedFilters = snapshotServerFilters(next);
	} else if (name === "partnerTypeId") {
		next.partnerTypeName = lookup.partnerTypes?.get(next.partnerTypeId)?.value ?? null;
		next.partnerId = null;
		next.partnerName = null;
	} else if (name === "partnerId") {
		next.partnerName = lookup.partners?.get(next.partnerId)?.value ?? null;
	}
	return { ...next, name: buildLayerName(next, lookup) };
}

export function isLayerVisible(layer: LayerDefinition) {
	return layer.visible !== false;
}

export function buildLayerName(layer: LayerDefinition, lookup?: LayerLookupData): string {
	const view = layer.itemType ?? "Layer";
	const parts = [view === "ISOs" ? view : splitWordsOnUpperCase(view)];

	const segs = layerNameSegments(layer, lookup);
	if (segs.length) parts.push(segs.join("; "));

	const dates = layer.dateRange ? [] : [dateOf(layer.startDateTime), dateOf(layer.endDateTime)].filter(Boolean);
	if (dates.length) parts.push(dates.join(" - "));

	return parts.filter(Boolean).join(" - ");
}

function layerNameSegments(layer: LayerDefinition, lookup: LayerLookupData | undefined): string[] {
	const name = (items: KeyedArray<FilterItem> | undefined, id: string | number | null | undefined) =>
		id === 0 ? "Unassigned" : items?.get(id)?.value;
	const names = (
		items: KeyedArray<FilterItem> | undefined,
		ids: (string | number)[] | undefined,
		fallback?: string,
	) =>
		formatMultiSelect(
			ids?.map((id) => name(items, id)),
			fallback,
		);
	const statusNames = formatMultiSelect(layer.statusIds?.map((id) => lookup?.locationStatuses?.get(id)?.name));
	const marketerLabel = lookup?.verbiage?.marketer || "Marketer";
	const marketerNames = names(lookup?.marketers, layer.marketerIds, layer.marketerName);

	const segmentSegs = () =>
		labeled([
			[
				"Brand",
				formatMultiSelect(
					layer.brandIds?.map((id) => lookup?.brands?.get(id)?.value),
					layer.brandName,
				),
			],
			[marketerLabel, marketerNames],
			[
				"Channel",
				formatMultiSelect(
					layer.channelIds?.map((id) => lookup?.channels?.get(id)?.value),
					layer.channelName,
				),
			],
			[
				"State",
				formatMultiSelect(
					layer.stateIds?.map((id) => lookup?.states?.get(id)?.value),
					layer.stateName,
				),
			],
			["Status", statusNames],
		]);

	if (isPositionLayer(layer.itemType)) {
		const isReps = layer.itemType === "Representatives";
		const selected = isReps ? layer.reps : layer.machines;
		return compact([
			`${selected?.length || "All"}`,
			(isReps ? layer.activeRepOnly : layer.activeMachineOnly) && "Active",
			...segmentSegs(),
			layer.enrollmentId && `Enrollment: ${layer.enrollmentId}`,
			layer.minAccuracy && `Accuracy: ${layer.minAccuracy}m`,
			dateRangeLabel(layer.dateRange),
			layer.lastPositionOnly && layer.hourLimit && `Within Last: ${layer.hourLimit} hours`,
			layer.lastPositions && `Limit: ${layer.lastPositions}`,
			layer.lastPositionOnly ? "Latest Position" : layer.showLines && "Paths",
		]);
	}

	if (layer.itemType === "Sales") {
		return compact([
			layer.enrollmentId && `Enrollment: ${layer.enrollmentId}`,
			dateRangeLabel(layer.dateRange),
			...segmentSegs(),
			layer.reps?.length && `Reps: ${layer.reps.length}`,
			layer.machines?.length && `Machines: ${layer.machines.length}`,
			layer.phoneCarrierType && `Phone: ${camelToStartCase(layer.phoneCarrierType)}`,
			layer.goodSales && "Good: Yes",
		]);
	}

	if (isOfficeLayer(layer.itemType)) {
		return compact([...segmentSegs(), activeStatusLabel(layer.statusId)]);
	}

	if (isRetailLayer(layer.itemType)) {
		const retailSegments = labeled([
			[
				lookup?.verbiage?.partnerType || "Partner Type",
				layer.partnerTypeName ||
					(layer.partnerTypeId ? lookup?.partnerTypes?.get(layer.partnerTypeId)?.value : ""),
			],
			[
				lookup?.verbiage?.partner || "Partner",
				layer.partnerName || (layer.partnerId ? lookup?.partners?.get(layer.partnerId)?.value : ""),
			],
			["Division", names(lookup?.companyDivisions, layer.companyDivisionIds, layer.companyDivisionName)],
			["Region", names(lookup?.companyRegions, layer.companyRegionIds, layer.companyRegionName)],
			["Market", names(lookup?.companyMarkets, layer.companyMarketIds, layer.companyMarketName)],
			["Status", statusNames],
			[marketerLabel, marketerNames],
			["Brand", layer.brandName || (layer.brandId ? lookup?.brands?.get(layer.brandId)?.value : "")],
			["Channel", layer.channelName || (layer.channelId ? lookup?.channels?.get(layer.channelId)?.value : "")],
			["State", layer.stateName || (layer.stateId ? lookup?.states?.get(layer.stateId)?.value : "")],
			[
				"Assigned AE",
				formatMultiSelect(
					layer.assignedUserIds?.map((id) => lookup?.users?.get(id)?.value),
					layer.assignedUserName,
				),
			],
			["Assigned Office", names(lookup?.offices, layer.assignedOfficeIds, layer.assignedOfficeName)],
		]);
		return compact([...retailSegments, activeStatusLabel(layer.statusId)]);
	}

	return [];
}

function activeStatusLabel(statusId: number | undefined) {
	if (!statusId) return undefined;
	return statusId === 1 ? "Active" : "Inactive";
}

function compact(segments: unknown[]) {
	return segments.filter((segment): segment is string => typeof segment === "string" && segment !== "");
}

function labeled(segments: [label: string, value: string | undefined][]) {
	return segments.filter(([, value]) => value).map(([label, value]) => `${label}: ${value}`);
}

function formatMultiSelect(items: (string | undefined)[] | undefined, fallback?: string) {
	const filtered = items?.filter(Boolean) ?? [];

	if (filtered.length > 2) return `${filtered.length} items`;
	if (filtered.length > 0) return filtered.join(", ");
	return fallback ?? "";
}
